import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth, storage } from "../../firebase.init";
import { collection, query, where, getDocs, doc, updateDoc, setDoc, getDoc, deleteDoc, serverTimestamp, limit, startAfter, orderBy, getCountFromServer, collectionGroup } from "firebase/firestore";
import "./AnnotateImages.css";
import defaultImage from '../Assets/default.png';
import ClipLoader from "react-spinners/ClipLoader";
import { useAuthState } from 'react-firebase-hooks/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faFilter, faCircleCheck, faRobot } from '@fortawesome/free-solid-svg-icons';
import Select from "react-select";
import axios from "axios";
import AnnotateModal from "../AnnotateModal/AnnotateModal"

const CLASS_COLORS = {
  door: "#f44336",
  glass: "#00bcd4",
  safety_glass: "#0097a7",
  letter_box: "#ffeb3b",
  knocker: "#8d6e63",
  spyhole: "#9c27b0",
  door_bell: "#fbc02d",
  hinge: "#009688",
  door_closer: "#4caf50",
  fire_exit: "#2e7d32",
  keep_shut: "#ef5350",
  signage: "#ffca28",
  door_damage: "#d32f2f",
  frame_damage: "#e91e63",
  door_handle: "#fb8c00",
  night_latch: "#ff9800",
  yale_lock: "#795548",
  mortice_lock: "#3f51b5",
  keyless_exit: "#00bcd4",
  code_lock: "#2196f3",
  bolt_latch: "#ff7043",
  mortice_cover: "#7b1fa2",
  emergency_lock: "#9c27b0",
  padlock: "#607d8b",
  push_pad: "#ff5722",
  door_knob: "#fb8c00",
  ventilation: "#90a4ae",
  keep_clear: "#26c6da",
  external_lock: "#ff6f00",
  door_latch: "#558b2f",

  normal: "#4caf50",
  missing: "#f44336",
  reflection: "#ff9800",
  loose: "#66bb6a",

  fd30_sticker: "#ab47bc",
  fd30_stamp: "#8e24aa",
  fd60_sticker: "#6a1b9a",
  fd60_stamp: "#4a148c"
};

const AnnotateImages = () => {
  const [pendingDoors, setPendingDoors] = useState([]);
  const [completedDoors, setCompletedDoors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [allBuildings, setAllBuildings] = useState([]);

  const [activeTab, setActiveTab] = useState('unannotated');

  const [selectedBuilding, setSelectedBuilding] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const [showManualAnnotateModal, setShowManualAnnotateModal] = useState(false);
  const [showAnnotateModalResult, setShowAnnotateModalResult] = useState(null);
  const [currentAnnotatingDoor, setCurrentAnnotatingDoor] = useState(null);
  const [processingDoors, setProcessingDoors] = useState(new Set());
  const [savingDoors, setSavingDoors] = useState(new Set());

  const scrollPositionRef = useRef(0);
  const [user, loadingAuth] = useAuthState(auth);

  const PAGE_SIZE = 50;

  const [pendingVisibleCount, setPendingVisibleCount] = useState(PAGE_SIZE);
  const [completedVisibleCount, setCompletedVisibleCount] = useState(PAGE_SIZE);

  const [loadingMorePending, setLoadingMorePending] = useState(false);
  const [loadingMoreCompleted, setLoadingMoreCompleted] = useState(false);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [loading]);

  const isDoorFullyAnnotated = (door) => {
    if (!door.annotations) return false;
    const expectedAnnotations = [];

    if (door.doorPushPhotoLink) expectedAnnotations.push('push');
    if (door.doorPullPhotoLink) expectedAnnotations.push('pull');
    if (door.hingePhotoLink) expectedAnnotations.push('hinge');
    // if (door.doorCertPhotoLink) expectedAnnotations.push('cert');

    if (expectedAnnotations.length === 0) return false;

    return expectedAnnotations.every(type => !!door.annotations[type]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear().toString().slice(-2);
    const hours = ("0" + date.getHours()).slice(-2);
    const minutes = ("0" + date.getMinutes()).slice(-2);
    const seconds = ("0" + date.getSeconds()).slice(-2);
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const buildingOptions = allBuildings.map((building) => ({
    value: building.buildingId,
    label: `${building.buildingName} (${building.reportId})`,
  }));

  const handleBuildingChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedBuilding(selectedOption.value);
    } else {
      setSelectedBuilding('all');
    }
  };

  const doorTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'communal', label: 'Communal Only' },
    { value: 'residential', label: 'Residential Only' },
  ];

  const handleDoorTypeChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedType(selectedOption.value);
    } else {
      setSelectedType('all');
    }
  };

  const selectStyles = {
    control: (provided, { isFocused }) => ({
      ...provided,
      borderRadius: "0.375rem",
      padding: "0 0.5rem",
      borderColor: isFocused ? "#dc3545" : "#ced4da",
      boxShadow: isFocused ? "0 0 0 0.2rem rgba(220,53,69,.25)" : "none",
      minHeight: "48px",
      height: "48px",
      outline: "none",
      "&:hover": { borderColor: "#dc3545" },
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      maxWidth: "600px",
    }),
    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      backgroundColor: isSelected
        ? "#dc3545"
        : isFocused
          ? "#ffe5e5"
          : "white",
      color: isSelected ? "white" : "#333",
      cursor: "pointer",
      minHeight: "38px",
      display: "flex",
      alignItems: "center",
      "&:active": { backgroundColor: "#dc3545" },
    }),
    singleValue: provided => ({
      ...provided,
      color: "#282828ff",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    placeholder: provided => ({
      ...provided,
      color: "#282828ff",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    dropdownIndicator: provided => ({
      ...provided,
      color: "#a71d2a",
      "&:hover": { color: "#dc3545" },
    }),
    clearIndicator: provided => ({
      ...provided,
      color: "#a71d2a",
      "&:hover": { color: "#dc3545" },
    }),
    menuPortal: base => ({ ...base, zIndex: 99999 }),
  };

  const refreshDoorAnnotations = async (door) => {
    const collectionType = door.type === "residential" ? "ResidentialReports" : "DoorReports";
    const annotations = {};

    try {
      if (door.doorPushPhotoLink) {
        const pushSnap = await getDoc(doc(db, `Reports/${door.buildingId}/${collectionType}/${door.id}/annotations/pushAiAnnotations`));
        if (pushSnap.exists()) {
          annotations.push = pushSnap.data();
        }
      }

      if (door.doorPullPhotoLink) {
        const pullSnap = await getDoc(doc(db, `Reports/${door.buildingId}/${collectionType}/${door.id}/annotations/pullAiAnnotations`));
        if (pullSnap.exists()) {
          annotations.pull = pullSnap.data();
        }
      }

      if (door.hingePhotoLink) {
        const hingeSnap = await getDoc(doc(db, `Reports/${door.buildingId}/${collectionType}/${door.id}/annotations/hingeAiAnnotations`));
        if (hingeSnap.exists()) {
          annotations.hinge = hingeSnap.data();
        }
      }

      // if (door.doorCertPhotoLink) {
      //   const certSnap = await getDoc(doc(db, `Reports/${door.buildingId}/${collectionType}/${door.id}/annotations/certAiAnnotations`));
      //   if (certSnap.exists()) {
      //     annotations.cert = certSnap.data();
      //   }
      // }

      return annotations;
    } catch (error) {
      console.error("Error refreshing door annotations:", error);
      return {};
    }
  };

  const getFilteredBuildings = () => {
    if (selectedBuilding === 'all') {
      return allBuildings;
    }
    return allBuildings.filter(b => b.buildingId === selectedBuilding);
  };

  const loadAllDoors = async (buildings) => {
    const doorPromises = buildings.map(async (building) => {
      const buildingId = building.buildingId;
      const buildingData = building;
      const buildingDoors = [];
      const promises = [];

      if (selectedType === 'all' || selectedType === 'communal') {
        promises.push(
          getDocs(query(collection(db, `Reports/${buildingId}/DoorReports`), where("addedBy", ">", "")))
            .then(snapshot => {
              const doors = snapshot.docs.map(doorDoc => ({
                id: doorDoc.id,
                type: "communal",
                buildingId,
                buildingName: buildingData.buildingName,
                buildingDateCreated: buildingData.dateCreated || buildingData.date,
                ...doorDoc.data()
              }));
              buildingDoors.push(...doors);
            })
        );
      }

      if (selectedType === 'all' || selectedType === 'residential') {
        promises.push(
          getDocs(query(collection(db, `Reports/${buildingId}/ResidentialReports`), where("addedBy", ">", "")))
            .then(snapshot => {
              const doors = snapshot.docs.map(resDoc => ({
                id: resDoc.id,
                type: "residential",
                buildingId,
                buildingName: buildingData.buildingName,
                buildingDateCreated: buildingData.dateCreated || buildingData.date,
                ...resDoc.data()
              }));
              buildingDoors.push(...doors);
            })
        );
      }

      await Promise.all(promises);
      return buildingDoors;
    });

    const doorArrays = await Promise.all(doorPromises);
    const allDoors = doorArrays.flat();

    const annotationsSnapshot = await getDocs(collectionGroup(db, 'annotations'));

    const annotationMap = new Map();

    annotationsSnapshot.docs.forEach(annotationDoc => {
      const path = annotationDoc.ref.path;
      const pathParts = path.split('/');
      const buildingId = pathParts[1];
      const collectionType = pathParts[2];
      const doorId = pathParts[3];
      const annotationType = pathParts[5];

      const doorKey = `${buildingId}-${collectionType}-${doorId}`;

      if (!annotationMap.has(doorKey)) {
        annotationMap.set(doorKey, { annotations: {}, latestAnnotationTime: 0 });
      }

      const typeMap = {
        'pushAiAnnotations': 'push',
        'pullAiAnnotations': 'pull',
        'hingeAiAnnotations': 'hinge',
        // 'certAiAnnotations': 'cert'
      };

      const simpleName = typeMap[annotationType] || annotationType;
      const data = annotationDoc.data();

      annotationMap.get(doorKey).annotations[simpleName] = data;

      const rawDate = data?.updatedAt || data?.createdAt || null;
      if (rawDate) {
        const t = rawDate?.toDate ? rawDate.toDate().getTime() : new Date(String(rawDate).replace(' ', 'T')).getTime() || 0;
        if (t > annotationMap.get(doorKey).latestAnnotationTime) {
          annotationMap.get(doorKey).latestAnnotationTime = t;
        }
      }
    });

    const getTime = (val) => {
      if (!val) return 0;
      if (val?.toDate) return val.toDate().getTime();
      if (typeof val === 'string') return new Date(val.replace(' ', 'T')).getTime() || 0;
      if (typeof val === 'number') return val;
      return 0;
    };

    const doorsWithAnnotations = allDoors.map(door => {
      const collectionType = door.type === "residential" ? "ResidentialReports" : "DoorReports";
      const doorKey = `${door.buildingId}-${collectionType}-${door.id}`;

      const { annotations = {}, latestAnnotationTime = 0 } = annotationMap.get(doorKey) || {};

      const bestTime = Math.max(
        latestAnnotationTime,
        getTime(door.dateCreated),
        getTime(door.date),
        getTime(door.buildingDateCreated),
      );

      return {
        ...door,
        annotations,
        annotationDate: latestAnnotationTime > 0 ? latestAnnotationTime : null,
        _sortTime: bestTime,
      };
    });

    const sortByDate = (a, b) => b._sortTime - a._sortTime;

    const pending = [];
    const completed = [];

    doorsWithAnnotations.forEach(door => {
      if (isDoorFullyAnnotated(door)) {
        completed.push(door);
      } else {
        pending.push(door);
      }
    });

    pending.sort(sortByDate);
    completed.sort(sortByDate);

    return { pending, completed };
  };

  useEffect(() => {
    const loadInitialDoors = async () => {
      setLoading(true);
      try {

        const reportsCollection = collection(db, "Reports");
        const reportsSnapshot = await getDocs(reportsCollection);

        const buildingsList = reportsSnapshot.docs
          .map((doc) => ({
            buildingId: doc.id,
            buildingName: doc.data().buildingName || "Unknown",
            dateCreated: doc.data().dateCreated,
            date: doc.data().date,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const dateA = a.dateCreated || a.date || '';
            const dateB = b.dateCreated || b.date || '';
            return dateB.localeCompare(dateA);
          });

        setAllBuildings(buildingsList);

        if (buildingsList.length === 0) {
          setLoading(false);
          return;
        }

        const { pending, completed } = await loadAllDoors(buildingsList);

        setPendingDoors(pending);
        setCompletedDoors(completed);

        setPendingVisibleCount(PAGE_SIZE);
        setCompletedVisibleCount(PAGE_SIZE);


      } catch (error) {
        console.error("Error loading doors:", error);
        toast.error("Error loading doors: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (user && !loadingAuth) {
      loadInitialDoors();
    }
  }, [user, loadingAuth]);

  const applyFilters = async () => {
    setLoading(true);

    try {
      const filteredBuildings = getFilteredBuildings();

      if (filteredBuildings.length === 0) {
        setPendingDoors([]);
        setCompletedDoors([]);
        setLoading(false);
        return;
      }

      const { pending, completed } = await loadAllDoors(filteredBuildings);

      setPendingDoors(pending);
      setCompletedDoors(completed);

    } catch (error) {
      console.error("Error applying filters:", error);
      toast.error("Error applying filters: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSelectedBuilding('all');
    setSelectedType('all');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getCurrentDoors = () => {
    if (activeTab === 'unannotated') {
      return pendingDoors.slice(0, pendingVisibleCount);
    }
    return completedDoors.slice(0, completedVisibleCount);
  };


  const compressBase64Image = (
    base64,
    {
      maxWidth = 1600,
      maxHeight = 1600,
      quality = 0.75,
      mimeType = "image/jpeg",
    } = {}
  ) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.src = `data:image/png;base64,${base64}`;

      img.onload = () => {
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const compressedDataUrl = canvas.toDataURL(mimeType, quality);

        resolve(compressedDataUrl.split(",")[1]);
      };

      img.onerror = reject;
    });

  const stripDataUrlPrefix = (dataUrl) => {
    if (!dataUrl) return null;
    const match = dataUrl.match(/^data:image\/\w+;base64,(.*)$/);
    return match ? match[1] : dataUrl;
  };

  const saveAIImage = async ({
    base64Image,
    fileName,
    firestoreField,
    annotationField,
    regions = [],
    onSuccess,
    door,
  }) => {
    if (!base64Image || !door) return;

    const doorKey = `${door.buildingId}-${door.id}-${annotationField}`;
    if (savingDoors.has(doorKey)) return;

    setSavingDoors(prev => new Set(prev).add(doorKey));

    try {
      const collectionType = door.type === "residential" ? "ResidentialReports" : "DoorReports";

      // const rawBase64 = stripDataUrlPrefix(base64Image);

      // const compressedBase64 = await compressBase64Image(rawBase64, {
      //   maxWidth: 1600,
      //   maxHeight: 1600,
      //   quality: 0.90,
      //   mimeType: "image/jpeg",
      // });

      // const storageRef = ref(
      //   storage,
      //   `Reports/${door.buildingId}/${collectionType}/${door.id}/${fileName}`
      // );

      // await uploadString(storageRef, compressedBase64, "base64", {
      //   contentType: "image/jpeg",
      // });

      // const downloadURL = await getDownloadURL(storageRef);

      const mappedRegions = regions.map(region => {
        const commonData = {
          cls: region.cls || '',
          type: region.type || 'box',
        };

        if (region.type === 'polygon') {
          const pointsX = [];
          const pointsY = [];
          (region.points || []).forEach(p => {
            pointsX.push(p[0]);
            pointsY.push(p[1]);
          });
          return {
            ...commonData,
            type: 'polygon',
            pointsX,
            pointsY,
          };
        } else {
          return {
            ...commonData,
            x: region.x || 0,
            y: region.y || 0,
            w: region.w || 0,
            h: region.h || 0,
          };
        }
      });

      // const docRef = doc(
      //   db,
      //   `Reports/${door.buildingId}/${collectionType}/${door.id}`
      // );

      // await updateDoc(docRef, {
      //   [firestoreField]: downloadURL,
      // });

      const annotationDocRef = doc(
        db,
        `Reports/${door.buildingId}/${collectionType}/${door.id}/annotations/${annotationField}`
      );

      const existingDoc = await getDoc(annotationDocRef);
      const createdAt = existingDoc.exists() && existingDoc.data()?.createdAt
        ? existingDoc.data()?.createdAt
        : serverTimestamp();

      await setDoc(annotationDocRef, {
        regions: mappedRegions,
        updatedAt: serverTimestamp(),
        createdAt,
      },
        { merge: true }
      );

      const updatedAnnotations = await refreshDoorAnnotations(door);

      const updatedDoor = {
        ...door,
        annotations: updatedAnnotations
      };

      const isNowFullyAnnotated = isDoorFullyAnnotated(updatedDoor);

      setPendingDoors(prevDoors => {
        if (isNowFullyAnnotated) {
          return prevDoors.filter(d =>
            !(d.id === door.id && d.buildingId === door.buildingId)
          );
        }
        return prevDoors.map(d =>
          d.id === door.id && d.buildingId === door.buildingId
            ? { ...d, annotations: updatedAnnotations }
            : d
        );
      });

      setCompletedDoors(prevDoors => {

        if (isNowFullyAnnotated) {

          const exists = prevDoors.some(d =>
            d.id === door.id && d.buildingId === door.buildingId
          );
          if (!exists) {
            return [updatedDoor, ...prevDoors];
          }
        }

        return prevDoors.map(d =>
          d.id === door.id && d.buildingId === door.buildingId
            ? { ...d, annotations: updatedAnnotations }
            : d
        );
      });

      onSuccess?.();
    } catch (error) {
      console.error("Failed to save AI image + annotations:", error);
      toast.error("Failed to save annotations: " + error.message);
    } finally {
      setSavingDoors(prev => {
        const newSet = new Set(prev);
        newSet.delete(doorKey);
        return newSet;
      });
    }
  };

  const handleViewAnnotation = async (door, annotationType) => {
    try {
      const collectionType = door.type === "residential" ? "ResidentialReports" : "DoorReports";

      let photoLink, annotationField, fileName, firestoreField, regionClsList, imageName;

      if (annotationType === 'push') {
        photoLink = door.doorPushPhotoLink;
        annotationField = 'pushAiAnnotations';
        fileName = 'push_photo_ai.png';
        firestoreField = 'pushPhotoAiLink';
        imageName = 'Push Door Photo';
        regionClsList = [
          "bolt_latch", "code_lock", "door", "door_bell", "door_closer", "door_damage",
          "door_handle", "door_knob", "door_latch ", "door_number", "drop_down_seal",
          "emergency_lock", "external_lock", "fire_exit", "frame_damage", "glass",
          "hinge", "keep_clear", "keep_locked", "keep_shut", "keyless_exit",
          "knocker", "letter_box", "mortice_cover", "mortice_lock", "night_latch",
          "padlock", "pull_handle", "push_pad", "safety_glass", "signage", "spyhole",
          "ventilation", "yale_lock",
        ];
      } else if (annotationType === 'pull') {
        photoLink = door.doorPullPhotoLink;
        annotationField = 'pullAiAnnotations';
        fileName = 'pull_photo_ai.png';
        firestoreField = 'pullPhotoAiLink';
        imageName = 'Pull Door Photo';
        regionClsList = [
          "bolt_latch", "code_lock", "door", "door_bell", "door_closer", "door_damage",
          "door_handle", "door_knob", "door_latch ", "door_number", "drop_down_seal",
          "emergency_lock", "external_lock", "fire_exit", "frame_damage", "glass",
          "hinge", "keep_clear", "keep_locked", "keep_shut", "keyless_exit",
          "knocker", "letter_box", "mortice_cover", "mortice_lock", "night_latch",
          "padlock", "pull_handle", "push_pad", "safety_glass", "signage", "spyhole",
          "ventilation", "yale_lock",
        ];
      } else if (annotationType === 'hinge') {
        photoLink = door.hingePhotoLink;
        annotationField = 'hingeAiAnnotations';
        fileName = 'hinge_photo_ai.png';
        firestoreField = 'hingePhotoAiLink';
        imageName = 'Hinge Photo';
        regionClsList = [
          "loose", "missing", "normal", "reflection"
        ];
      }
      // else if (annotationType === 'cert') {
      //   photoLink = door.doorCertPhotoLink;
      //   annotationField = 'certAiAnnotations';
      //   fileName = 'cert_photo_ai.png';
      //   firestoreField = 'certPhotoAiLink';
      //   imageName = 'Certificate Photo';
      //   regionClsList = [
      //     "fd30_sticker", "fd30_stamp", "fd60_sticker", "fd60_stamp"
      //   ];
      // }

      if (!photoLink) {
        toast.warning(`No ${annotationType} photo available`);
        return;
      }

      const annotationDocRef = doc(
        db,
        `Reports/${door.buildingId}/${collectionType}/${door.id}/annotations/${annotationField}`
      );

      const annotationSnap = await getDoc(annotationDocRef);

      if (!annotationSnap.exists()) {
        toast.warning('No annotations found');
        return;
      }

      const annotationData = annotationSnap.data();
      let existingRegions = annotationData?.regions || [];

      existingRegions = existingRegions.map((region, index) => {
        const baseRegion = {
          ...region,
          id: `region_${index}`,
          color: CLASS_COLORS[region.cls] || '#ff0000',
          type: region.type || 'box',
        };

        if (region.type === 'polygon') {

          const pointsX = Array.isArray(region.pointsX) ? region.pointsX : [];
          const pointsY = Array.isArray(region.pointsY) ? region.pointsY : [];

          const reconstructedPoints = [];

          for (let i = 0; i < Math.min(pointsX.length, pointsY.length); i++) {
            reconstructedPoints.push([pointsX[i], pointsY[i]]);
          }

          baseRegion.points = reconstructedPoints;
        }

        return baseRegion;
      });

      setCurrentAnnotatingDoor(door);
      setShowAnnotateModalResult({
        regions: existingRegions || [],
        imageSrc: photoLink,
        regionClsList,
        fileName,
        imageName,
        firestoreField,
        annotationField,
      });
      setShowManualAnnotateModal(true);

    } catch (error) {
      console.error('Error loading annotation:', error);
      toast.error('Failed to load annotation: ' + error.message);
    }
  };


  const handleDetectDoorPull = async (door) => {
    if (!door.doorPullPhotoLink) {
      toast.warning("No pull photo available for this door");
      return;
    }

    const doorKey = `${door.buildingId}-${door.id}-pull`;
    if (processingDoors.has(doorKey)) return;

    setProcessingDoors(prev => new Set(prev).add(doorKey));

    try {
      setCurrentAnnotatingDoor(door);
      setShowAnnotateModalResult({
        regions: [],
        imageSrc: door.doorPullPhotoLink,
        imageName: "Pull Door Photo",
        regionClsList: [
          "bolt_latch", "code_lock", "door", "door_bell", "door_closer", "door_damage",
          "door_handle", "door_knob", "door_latch ", "door_number", "drop_down_seal",
          "emergency_lock", "external_lock", "fire_exit", "frame_damage", "glass",
          "hinge", "keep_clear", "keep_locked", "keep_shut", "keyless_exit",
          "knocker", "letter_box", "mortice_cover", "mortice_lock", "night_latch",
          "padlock", "pull_handle", "push_pad", "safety_glass", "signage", "spyhole",
          "ventilation", "yale_lock",
        ],
        fileName: "pull_photo_ai.png",
        firestoreField: "doorPullPhotoAiLink",
        annotationField: "pullAiAnnotations",
      });
      setShowManualAnnotateModal(true);

    } catch (err) {
      console.error("Error detecting door pull parts:", err);
      toast.error("Failed to detect door parts");
    } finally {
      setProcessingDoors(prev => {
        const newSet = new Set(prev);
        newSet.delete(doorKey);
        return newSet;
      });
    }
  };

  const handleDetectDoorPush = async (door) => {
    if (!door.doorPushPhotoLink) {
      toast.warning("No push photo available for this door");
      return;
    }

    const doorKey = `${door.buildingId}-${door.id}-push`;
    if (processingDoors.has(doorKey)) return;

    setProcessingDoors(prev => new Set(prev).add(doorKey));

    try {
      setCurrentAnnotatingDoor(door);
      setShowAnnotateModalResult({
        regions: [],
        imageSrc: door.doorPushPhotoLink,
        imageName: "Push Door Photo",
        regionClsList: [
          "bolt_latch", "code_lock", "door", "door_bell", "door_closer", "door_damage",
          "door_handle", "door_knob", "door_latch ", "door_number", "drop_down_seal",
          "emergency_lock", "external_lock", "fire_exit", "frame_damage", "glass",
          "hinge", "keep_clear", "keep_locked", "keep_shut", "keyless_exit",
          "knocker", "letter_box", "mortice_cover", "mortice_lock", "night_latch",
          "padlock", "pull_handle", "push_pad", "safety_glass", "signage", "spyhole",
          "ventilation", "yale_lock",
        ],
        fileName: "push_photo_ai.png",
        firestoreField: "doorPushPhotoAiLink",
        annotationField: "pushAiAnnotations",
      });
      setShowManualAnnotateModal(true);

    } catch (err) {
      console.error("Error detecting door push parts:", err);
      toast.error("Failed to detect door parts");
    } finally {
      setProcessingDoors(prev => {
        const newSet = new Set(prev);
        newSet.delete(doorKey);
        return newSet;
      });
    }
  };

  const handleDetectHinge = async (door) => {
    if (!door.hingePhotoLink) {
      toast.warning("No hinge photo available for this door");
      return;
    }

    const doorKey = `${door.buildingId}-${door.id}-hinge`;
    if (processingDoors.has(doorKey)) return;

    setProcessingDoors(prev => new Set(prev).add(doorKey));

    try {
      const blob = await (await fetch(door.hingePhotoLink)).blob();

      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
        });

      const base64Image = await toBase64(blob);

      const response = await axios.post(
        "https://firechecked-model-server-813451531907.europe-west1.run.app/predict-hinge-screw",
        { image: base64Image },
        { headers: { "X-Requested-With": "XMLHttpRequest" } }
      );

      const regionsWithColors = (response.data.regions || []).map((region, index) => ({
        ...region,
        id: `ai_${index}`,
        color: CLASS_COLORS[region.cls] || '#ff0000',
        type: region.type || 'box'
      }));

      setCurrentAnnotatingDoor(door);
      setShowAnnotateModalResult({
        ...response.data,
        regions: regionsWithColors,
        imageSrc: door.hingePhotoLink,
        imageName: "Hinge Photo",
        regionClsList: [
          "loose", "missing", "normal", "reflection"
        ],
        fileName: "hinge_photo_ai.png",
        firestoreField: "hingePhotoAiLink",
        annotationField: "hingeAiAnnotations",
      });
      setShowManualAnnotateModal(true);

    } catch (err) {
      console.error("Error detecting hinge screws:", err);
      toast.error("Failed to detect hinge screws");
    } finally {
      setProcessingDoors(prev => {
        const newSet = new Set(prev);
        newSet.delete(doorKey);
        return newSet;
      });
    }
  };

  // const handleDetectCert = async (door) => {
  //   if (!door.doorCertPhotoLink) {
  //     toast.warning("No cert photo available for this door");
  //     return;
  //   }

  //   const doorKey = `${door.buildingId}-${door.id}-cert`;
  //   if (processingDoors.has(doorKey)) return;

  //   setProcessingDoors(prev => new Set(prev).add(doorKey));

  //   try {

  //     setCurrentAnnotatingDoor(door);
  //     setShowAnnotateModalResult({
  //       regions: [],
  //       imageSrc: door.doorCertPhotoLink,
  //       imageName: "Certificate Photo",
  //       regionClsList: [
  //         "fd30_sticker", "fd30_stamp", "fd60_sticker", "fd60_stamp"
  //       ],
  //       fileName: "cert_photo_ai.png",
  //       firestoreField: "doorCertPhotAiLink",
  //       annotationField: "certAiAnnotations",
  //     });
  //     setShowManualAnnotateModal(true);

  //   } catch (err) {
  //     console.error("Error detecting hinge screws:", err);
  //     toast.error("Failed to detect hinge screws");
  //   } finally {
  //     setProcessingDoors(prev => {
  //       const newSet = new Set(prev);
  //       newSet.delete(doorKey);
  //       return newSet;
  //     });
  //   }
  // };

  const [messages, setMessages] = useState([
    "Fetching all doors...",
    "Loading annotations...",
    "Almost there..."
  ]);

  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    const currentMessage = messages[messageIndex];

    if (charIndex < currentMessage.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(currentMessage.slice(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 30);
      return () => clearTimeout(timeout);
    } else {
      const delay = setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
        setCharIndex(0);
        setDisplayedText('');
      }, 1500);
      return () => clearTimeout(delay);
    }
  }, [charIndex, messageIndex]);

  const getAnnotationInfo = (door) => {
    const annotations = door.annotations ?? {};
    const expected = [];
    const found = [];

    if (door.doorPushPhotoLink) { expected.push('push'); if (annotations.push) found.push('push'); }
    if (door.doorPullPhotoLink) { expected.push('pull'); if (annotations.pull) found.push('pull'); }
    if (door.hingePhotoLink) { expected.push('hinge'); if (annotations.hinge) found.push('hinge'); }
    // if (door.doorCertPhotoLink) { expected.push('cert'); if (annotations.cert) found.push('cert'); }

    return { expected: expected.length, found: found.length };
  };

  if (loading) return (
    <div className="loading-container">
      <ClipLoader color="#b11111" size={60} />
      <div className="loading-text">
        {displayedText}
        <span className="cursor">|</span>
      </div>
    </div>
  );

  const currentDoors = getCurrentDoors();

  return (
    <div className="container">
      <ToastContainer />

      <AnnotateModal
        show={showManualAnnotateModal}
        imageSrc={showAnnotateModalResult?.imageSrc}
        regionClsList={showAnnotateModalResult?.regionClsList || []}
        regions={showAnnotateModalResult?.regions || []}
        imageName={showAnnotateModalResult?.imageName}
        onClose={() => {
          setShowManualAnnotateModal(false);
          setCurrentAnnotatingDoor(null);
          setShowAnnotateModalResult(null);
        }}
        onSave={({ regions, base64 }) => {
          if (!showAnnotateModalResult || !currentAnnotatingDoor) return;

          saveAIImage({
            base64Image: base64,
            fileName: showAnnotateModalResult.fileName,
            firestoreField: showAnnotateModalResult.firestoreField,
            annotationField: showAnnotateModalResult.annotationField,
            regions,
            door: currentAnnotatingDoor,
            onSuccess: () => {
              toast.success(
                `Annotation saved successfully!`,
                {
                  icon: <FontAwesomeIcon icon={faCircleCheck} size="lg" style={{ color: "green" }} />,
                  autoClose: 2000,
                  style: {
                    backgroundColor: "#e6ffed",
                    color: "#064e3b",
                    borderLeft: "4px solid green",
                    fontSize: "14px"
                  },
                }
              );
              setShowManualAnnotateModal(false);
              setCurrentAnnotatingDoor(null);
              setShowAnnotateModalResult(null);
            },
          });
        }}
      />

      <ul className="nav nav-tabs pt-4 mb-4">
        <li className="nav-item">
          <button
            className="nav-link"
            style={{
              color: activeTab === 'unannotated' ? '#dc3545' : '#6c757d',
              borderColor:
                activeTab === 'unannotated'
                  ? '#dc3545 #dc3545 #fff'
                  : 'transparent'
            }}
            onClick={() => handleTabChange('unannotated')}
          >
            Pending ({pendingDoors.length})
          </button>
        </li>

        <li className="nav-item">
          <button
            className="nav-link"
            style={{
              color: activeTab === 'annotated' ? '#dc3545' : '#6c757d',
              borderColor:
                activeTab === 'annotated'
                  ? '#dc3545 #dc3545 #fff'
                  : 'transparent'
            }}
            onClick={() => handleTabChange('annotated')}
          >
            Completed ({completedDoors.length})
          </button>
        </li>
      </ul>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>{activeTab === 'unannotated' ? 'Pending Annotations' : 'Completed Annotations'}</h2>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-danger"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={faFilter} /> {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="card p-3 mb-3">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Building</label>
                <Select
                  options={buildingOptions}
                  onChange={handleBuildingChange}
                  isClearable
                  placeholder="Select a building..."
                  value={buildingOptions.find(opt => opt.value === selectedBuilding) || null}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Door Type</label>
                <Select
                  options={doorTypeOptions}
                  onChange={handleDoorTypeChange}
                  isClearable
                  placeholder="Select door type..."
                  value={doorTypeOptions.find(opt => opt.value === selectedType) || null}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
            </div>

            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-danger" onClick={applyFilters}>
                Apply Filters
              </button>
              <button className="btn btn-secondary" onClick={resetFilters}>
                Reset
              </button>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center">
          <p className="mb-0">
            Showing <strong>{currentDoors.length}</strong> {activeTab === 'unannotated' ? 'pending' : 'completed'} doors
          </p>
        </div>
      </div>

      {currentDoors.length === 0 ? (
        <p className="text-center">
          {activeTab === 'unannotated'
            ? 'No doors pending review'
            : 'No completed annotations yet'}
        </p>
      ) : (
        <div className="row">
          {currentDoors.map((door, originalIndex) => {
            const {
              id,
              doorPullPhotoLink,
              doorLocation,
              doorNumber,
              annotations,
              type,
              buildingName,
              dateCreated,
              date,
              buildingDateCreated,
              annotationDate,
            } = door;

            const annotationInfo = getAnnotationInfo(door);

            return (
              <div
                className={`${isMobile ? "col-6" : "col-lg-3 col-md-6 col-12"} mb-4`}
                key={`${door.buildingId}-${id}`}
              >
                <div className="door-card" data-aos-anchor-placement="center-bottom">
                  <div className="card-image-wrapper">
                    <div className={`status-badge ${annotationInfo.found === annotationInfo.expected ? 'status-badge-complete' : 'status-badge-incomplete'}`}>
                      <div>{annotationInfo.found}/{annotationInfo.expected}</div>
                    </div>

                    <img
                      src={doorPullPhotoLink || defaultImage}
                      alt={doorLocation}
                      className={`card-image ${isMobile ? 'card-image-mobile' : 'card-image-desktop'}`}
                      loading="lazy"
                    />
                  </div>

                  <div className={`card-content ${isMobile ? 'card-content-mobile' : 'card-content-desktop'}`}>
                    <div className={`card-date ${isMobile ? 'card-date-mobile' : 'card-date-desktop'}`}>
                      {annotationDate
                        ? formatDate(new Date(annotationDate))
                        : date || buildingDateCreated
                          ? formatDate(date || buildingDateCreated)
                          : "No date"}
                    </div>

                    <div className="card-info-row">
                      <div className={`card-building ${isMobile ? 'card-building-mobile' : 'card-building-desktop'}`}>
                        {buildingName || "Unknown Building"}
                      </div>
                      <div className={`card-title ${isMobile ? 'card-title-mobile' : 'card-title-desktop'}`}>
                        {(type === "residential" ? "R" : "C") +
                          (doorNumber
                            ? doorNumber
                            : originalIndex != null
                              ? originalIndex + 1
                              : "N/A")}
                      </div>
                    </div>

                    <div className="badge-container">
                      {door.doorPushPhotoLink && (
                        <div
                          className={`annotation-badge ${isMobile ? 'annotation-badge-mobile' : 'annotation-badge-desktop'} ${annotations?.push
                            ? 'completed'
                            : savingDoors.has(`${door.buildingId}-${door.id}-pushAiAnnotations`)
                              ? 'saving'
                              : processingDoors.has(`${door.buildingId}-${door.id}-push`)
                                ? 'processing'
                                : ''
                            } ${savingDoors.has(`${door.buildingId}-${door.id}-pushAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-push`)
                              ? 'disabled'
                              : ''
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              savingDoors.has(`${door.buildingId}-${door.id}-pushAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-push`)
                            ) {
                              return;
                            }
                            if (annotations?.push) {
                              handleViewAnnotation(door, 'push');
                            } else {
                              handleDetectDoorPush(door);
                            }
                          }}
                          title={
                            savingDoors.has(`${door.buildingId}-${door.id}-pushAiAnnotations`)
                              ? 'Saving...'
                              : annotations?.push
                                ? 'Click to view/edit'
                                : 'Click to annotate'
                          }
                        >
                          {savingDoors.has(`${door.buildingId}-${door.id}-pushAiAnnotations`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Saving</div>
                            </>
                          ) : processingDoors.has(`${door.buildingId}-${door.id}-push`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Push</div>
                            </>
                          ) : (
                            <>
                              <div className="badge-icon">{annotations?.push ? '✓' : '○'}</div>
                              <div>Push</div>
                            </>
                          )}
                        </div>
                      )}

                      {door.doorPullPhotoLink && (
                        <div
                          className={`annotation-badge ${isMobile ? 'annotation-badge-mobile' : 'annotation-badge-desktop'} ${annotations?.pull
                            ? 'completed'
                            : savingDoors.has(`${door.buildingId}-${door.id}-pullAiAnnotations`)
                              ? 'saving'
                              : processingDoors.has(`${door.buildingId}-${door.id}-pull`)
                                ? 'processing'
                                : ''
                            } ${savingDoors.has(`${door.buildingId}-${door.id}-pullAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-pull`)
                              ? 'disabled'
                              : ''
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              savingDoors.has(`${door.buildingId}-${door.id}-pullAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-pull`)
                            ) {
                              return;
                            }
                            if (annotations?.pull) {
                              handleViewAnnotation(door, 'pull');
                            } else {
                              handleDetectDoorPull(door);
                            }
                          }}
                          title={
                            savingDoors.has(`${door.buildingId}-${door.id}-pullAiAnnotations`)
                              ? 'Saving...'
                              : annotations?.pull
                                ? 'Click to view/edit'
                                : 'Click to annotate'
                          }
                        >
                          {savingDoors.has(`${door.buildingId}-${door.id}-pullAiAnnotations`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Saving</div>
                            </>
                          ) : processingDoors.has(`${door.buildingId}-${door.id}-pull`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Pull</div>
                            </>
                          ) : (
                            <>
                              <div className="badge-icon">{annotations?.pull ? '✓' : '○'}</div>
                              <div>Pull</div>
                            </>
                          )}
                        </div>
                      )}

                      {door.hingePhotoLink && (
                        <div
                          className={`annotation-badge ${isMobile ? 'annotation-badge-mobile' : 'annotation-badge-desktop'} ${annotations?.hinge
                            ? 'completed'
                            : savingDoors.has(`${door.buildingId}-${door.id}-hingeAiAnnotations`)
                              ? 'saving'
                              : processingDoors.has(`${door.buildingId}-${door.id}-hinge`)
                                ? 'processing'
                                : ''
                            } ${savingDoors.has(`${door.buildingId}-${door.id}-hingeAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-hinge`)
                              ? 'disabled'
                              : ''
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              savingDoors.has(`${door.buildingId}-${door.id}-hingeAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-hinge`)
                            ) {
                              return;
                            }
                            if (annotations?.hinge) {
                              handleViewAnnotation(door, 'hinge');
                            } else {
                              handleDetectHinge(door);
                            }
                          }}
                          title={
                            savingDoors.has(`${door.buildingId}-${door.id}-hingeAiAnnotations`)
                              ? 'Saving...'
                              : annotations?.hinge
                                ? 'Click to view/edit'
                                : 'Click to annotate'
                          }
                        >
                          {savingDoors.has(`${door.buildingId}-${door.id}-hingeAiAnnotations`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Saving</div>
                            </>
                          ) : processingDoors.has(`${door.buildingId}-${door.id}-hinge`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Hinge</div>
                            </>
                          ) : (
                            <>
                              <div className="badge-icon">{annotations?.hinge ? '✓' : '○'}</div>
                              <div>Hinge</div>
                            </>
                          )}
                        </div>
                      )}

                      {/* {door.doorCertPhotoLink && (
                        <div
                          className={`annotation-badge ${isMobile ? 'annotation-badge-mobile' : 'annotation-badge-desktop'} ${annotations?.cert
                            ? 'completed'
                            : savingDoors.has(`${door.buildingId}-${door.id}-certAiAnnotations`)
                              ? 'saving'
                              : processingDoors.has(`${door.buildingId}-${door.id}-cert`)
                                ? 'processing'
                                : ''
                            } ${savingDoors.has(`${door.buildingId}-${door.id}-certAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-cert`)
                              ? 'disabled'
                              : ''
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              savingDoors.has(`${door.buildingId}-${door.id}-certAiAnnotations`) ||
                              processingDoors.has(`${door.buildingId}-${door.id}-cert`)
                            ) {
                              return;
                            }
                            if (annotations?.cert) {
                              handleViewAnnotation(door, 'cert');
                            } else {
                              handleDetectCert(door);
                            }
                          }}
                          title={
                            savingDoors.has(`${door.buildingId}-${door.id}-certAiAnnotations`)
                              ? 'Saving...'
                              : annotations?.cert
                                ? 'Click to view/edit'
                                : 'Click to annotate'
                          }
                        >
                          {savingDoors.has(`${door.buildingId}-${door.id}-certAiAnnotations`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Saving</div>
                            </>
                          ) : processingDoors.has(`${door.buildingId}-${door.id}-cert`) ? (
                            <>
                              <ClipLoader color="#fff" size={10} className="spinner" />
                              <div>Certificate</div>
                            </>
                          ) : (
                            <>
                              <div className="badge-icon">{annotations?.cert ? '✓' : '○'}</div>
                              <div>Certificate</div>
                            </>
                          )}
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {activeTab === 'unannotated' && pendingVisibleCount < pendingDoors.length && (
            <div className="text-center my-4">
              <button
                className="btn btn-outline-danger"
                disabled={loadingMorePending}
                onClick={() => {
                  if (loadingMorePending) return;

                  setLoadingMorePending(true);

                  setTimeout(() => {
                    setPendingVisibleCount(prev => prev + PAGE_SIZE);
                    setLoadingMorePending(false);
                  }, 5000);
                }}
              >
                {loadingMorePending ? (
                  <>
                    <ClipLoader size={14} color="#dc3545" /> Loading...
                  </>
                ) : (
                  `Load more (${Math.min(PAGE_SIZE, pendingDoors.length - pendingVisibleCount)})`
                )}
              </button>
            </div>
          )}
          {activeTab === 'annotated' && completedVisibleCount < completedDoors.length && (
            <div className="text-center my-4">
              <button
                className="btn btn-outline-danger"
                disabled={loadingMoreCompleted}
                onClick={() => {
                  if (loadingMoreCompleted) return;

                  setLoadingMoreCompleted(true);

                  setTimeout(() => {
                    setCompletedVisibleCount(prev => prev + PAGE_SIZE);
                    setLoadingMoreCompleted(false);
                  }, 5000);
                }}
              >
                {loadingMoreCompleted ? (
                  <>
                    <ClipLoader size={14} color="#dc3545" /> Loading...
                  </>
                ) : (
                  `Load more (${Math.min(PAGE_SIZE, completedDoors.length - completedVisibleCount)})`
                )}
              </button>
            </div>
          )}


        </div>

      )}
    </div>
  );
};

export default AnnotateImages;